import { useMemo } from 'react';
import type { Layer } from '@deck.gl/core';
import type { Position } from 'geojson';
import { HeatmapLayer } from '@deck.gl/aggregation-layers';
import { useAgentsData } from '../../data/useAgentsData';
import { useAggregationConfig } from '../useAggregationConfig';
import { useMapColorPalette } from '../useMapColorPalette';
import { useMapVisibility } from '../useMapVisibility';
import type { Agent, Trip } from '../../../types/domain';
import { alpha, type RgbColor } from '../../../utils/color';
import { useTripsData } from '../../data/useTripsData';

type UseHeatmapLayer = {
  heatmapLayer: Layer;
};

const useHeatmapLayer = (): UseHeatmapLayer => {
  const { aggregationConfig } = useAggregationConfig();
  const { isAggregationVisible } = useMapVisibility();
  const { agents } = useAgentsData();
  const { trips } = useTripsData();
  const { getRgbColors, mapColorOpacity } = useMapColorPalette();

  const data = useMemo((): Position[] => {
    switch (aggregationConfig?.target) {
      case 'home':
        return agents
          .filter(
            (
              agent: Agent
            ): agent is Agent & { home_lon: number; home_lat: number } => {
              return (
                typeof agent.home_lon === 'number' &&
                typeof agent.home_lat === 'number'
              );
            }
          )
          .map(
            (
              agent: Agent & { home_lon: number; home_lat: number }
            ): Position => {
              return [agent.home_lon, agent.home_lat];
            }
          );
      case 'work':
        return agents
          .filter(
            (
              agent: Agent
            ): agent is Agent & { work_lon: number; work_lat: number } => {
              return (
                typeof agent.work_lon === 'number' &&
                typeof agent.work_lat === 'number'
              );
            }
          )
          .map(
            (
              agent: Agent & { work_lon: number; work_lat: number }
            ): Position => {
              return [agent.work_lon, agent.work_lat];
            }
          );
      case 'trip_start':
      case 'stop_by':
        return trips.map((trip: Trip): Position => {
          return [trip.start_lon, trip.start_lat];
        });
      case 'trip_end':
        return trips.map((trip: Trip): Position => {
          return [trip.end_lon, trip.end_lat];
        });
      default:
        return [];
    }
  }, [aggregationConfig, agents, trips]);

  const colors = useMemo((): RgbColor[] => {
    return getRgbColors(8).map((color: RgbColor): RgbColor => {
      return alpha(color, 1);
    });
  }, [getRgbColors]);

  const heatmapLayer = new HeatmapLayer<Position>({
    id: 'heatmap-layer',
    data: data,
    getPosition: (position: Position): [number, number] => {
      return [position[0], position[1]];
    },
    colorRange: colors,
    opacity: mapColorOpacity,
    threshold: 0.03,
    pickable: false,
    visible: isAggregationVisible && aggregationConfig?.type === 'heatmap',
  });

  return { heatmapLayer };
};

export { useHeatmapLayer };
